// Tabla comparativa: una fila por equipo, una columna por variable del golden.
//
// Por qué existe aparte de la tabla de deriva: aquélla da una fila por HALLAZGO
// —sirve para trabajar lo que está mal— y ésta da una fila por EQUIPO con todas
// sus variables, que es lo que responde "¿cómo está la flota?" de un vistazo.
//
// La regla que manda en el diseño: **una celda sin datos no se pinta como
// conforme**. Un equipo que no se pudo cosechar no está bien, es que no se sabe;
// pintarlo con palomita es exactamente el error que esta pantalla existe para
// no cometer.
import { useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  useComparativa,
  type CampoComparativa,
  type EquipoComparativa,
  type EstadoCelda,
} from "@/hooks/useComparativa";

function Marca({ estado }: { estado: EstadoCelda }) {
  if (estado === "ok") {
    return (
      <span className="text-emerald-600 dark:text-emerald-400" title="Coincide con el golden">
        ✓
      </span>
    );
  }
  if (estado === "deriva") {
    return (
      <span className="font-bold text-red-600 dark:text-red-400" title="No coincide con el golden">
        ✕
      </span>
    );
  }
  return (
    <span className="text-muted-foreground" title="No se pudo leer: no se sabe">
      –
    </span>
  );
}

function textoValor(v: unknown): string {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "sí" : "no";
  return String(v);
}

// Contexto operativo: sale del inventario de agencias, no del golden. Va a la
// izquierda porque es lo que convierte un hallazgo en una accion — saber que
// Colima no cumple sirve poco si no sabes que se llega por VMS1 y que su unica
// via de reinicio es fisica.
const CONTEXTO = [
  { key: "concentrador", label: "Concentrador" },
  { key: "via_reinicio", label: "Vía de reinicio" },
  { key: "operador", label: "Operador" },
];

const FILTROS: { id: "todos" | EstadoCelda; etiqueta: string }[] = [
  { id: "todos", etiqueta: "Todos" },
  { id: "deriva", etiqueta: "Con diferencias" },
  { id: "sin_datos", etiqueta: "Sin datos" },
  { id: "ok", etiqueta: "Conformes" },
];

export function TablaComparativa() {
  const { data, isPending, error } = useComparativa();
  const [filtro, setFiltro] = useState<"todos" | EstadoCelda>("todos");
  const [detalle, setDetalle] = useState(false);

  const equipos = useMemo(
    () => (data?.equipos ?? []).filter((e) => filtro === "todos" || e.estado === filtro),
    [data, filtro],
  );

  if (isPending) {
    return (
      <div className="rounded-md border bg-card p-8 text-center text-sm text-muted-foreground">
        Cargando comparativa…
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="rounded-md border bg-card p-8 text-center text-sm text-muted-foreground">
        No se pudo cargar la comparativa.
      </div>
    );
  }

  const { resumen, campos } = data;
  const golden = campos.filter((c) => c.tipo === "golden");
  const presencia = campos.filter((c) => c.tipo === "presencia");

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-4">
        <Resumen n={resumen.total} etiqueta="Equipos" />
        <Resumen n={resumen.conformes} etiqueta="Conformes" tono="ok" />
        <Resumen n={resumen.con_deriva} etiqueta="Con diferencias" tono="mal" />
        <Resumen
          n={resumen.sin_datos}
          etiqueta="Sin datos"
          tono="gris"
          ayuda="No se pudieron cosechar. No se sabe cómo están."
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {FILTROS.map((f) => (
          <Button
            key={f.id}
            size="sm"
            variant={filtro === f.id ? "default" : "outline"}
            onClick={() => setFiltro(f.id)}
          >
            {f.etiqueta}
          </Button>
        ))}
        <Button size="sm" variant="outline" onClick={() => setDetalle((d) => !d)}>
          {detalle ? "Ver marcas" : "Ver valores"}
        </Button>
        <span className="ml-auto text-xs text-muted-foreground">
          {equipos.length} de {resumen.total}
        </span>
      </div>

      <div className="overflow-x-auto rounded-md border bg-card">
        <table className="w-full text-sm">
          <thead>
            {/* Dos grupos, y la separación NO es cosmética: en el primero una
                cruz significa "no coincide con el golden"; en el segundo,
                "no se capturó el dato". Sin decirlo, la misma marca se leería
                de dos maneras distintas. */}
            <tr className="border-b text-[11px] uppercase tracking-wide text-muted-foreground">
              <th className="sticky left-0 z-10 bg-card px-3 py-1.5" />
              <th colSpan={CONTEXTO.length} className="px-3 py-1.5 text-left font-medium">
                Cómo se llega
              </th>
              <th
                colSpan={golden.length}
                className="border-l px-3 py-1.5 text-center font-medium"
              >
                Contra el golden
              </th>
              <th
                colSpan={presencia.length}
                className="border-l px-3 py-1.5 text-center font-medium"
              >
                ¿Se capturó el dato?
              </th>
            </tr>
            <tr className="border-b bg-muted/40 text-left">
              <th className="sticky left-0 z-10 bg-muted/40 px-3 py-2 font-medium">Equipo</th>
              {CONTEXTO.map((c) => (
                <th key={c.key} className="px-3 py-2 font-medium">
                  {c.label}
                </th>
              ))}
              {[...golden, ...presencia].map((c, i) => (
                <th
                  key={c.key}
                  className={`px-3 py-2 text-center font-medium ${
                    i === 0 || i === golden.length ? "border-l" : ""
                  }`}
                >
                  <div>{c.label}</div>
                  {/* El esperado a la vista: una cruz sin el golden al lado
                      sólo dice "algo está mal", no qué debería ser. */}
                  <div className="mt-0.5 font-mono text-[10px] font-normal text-muted-foreground">
                    {c.tipo === "golden" ? textoValor(c.esperado) : "varía por sitio"}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {equipos.map((e) => (
              <Fila key={e.device_id} equipo={e} campos={campos} detalle={detalle} />
            ))}
            {equipos.length === 0 && (
              <tr>
                <td
                  colSpan={campos.length + CONTEXTO.length + 1}
                  className="px-3 py-8 text-center text-muted-foreground"
                >
                  Ningún equipo con ese filtro.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-muted-foreground">
        <strong>Contra el golden:</strong> ✓ coincide · ✕ no coincide. <strong>¿Se capturó?</strong>{" "}
        ✓ el dato vino · ✕ vino vacío — esas variables cambian de sitio a sitio a propósito, así
        que no se comparan contra un valor único. En ambos grupos, <strong>–</strong> significa que
        no se pudo cosechar el equipo: no se sabe, que no es lo mismo que estar bien.
      </p>
    </div>
  );
}

function Fila({
  equipo,
  campos,
  detalle,
}: {
  equipo: EquipoComparativa;
  campos: CampoComparativa[];
  detalle: boolean;
}) {
  const gris = equipo.estado === "sin_datos";
  return (
    <tr className={`border-b last:border-0 ${gris ? "opacity-70" : ""}`}>
      <td className="sticky left-0 z-10 bg-card px-3 py-2">
        <div className="font-medium">{equipo.sitio}</div>
        <div className="font-mono text-[11px] text-muted-foreground">{equipo.ip}</div>
        {gris && equipo.motivo && (
          // Decir POR QUE no se pudo leer, para no acusar al equipo: "offline"
          // aquí significa que AROC no pudo capturar, no que esté apagado.
          <div className="mt-0.5 max-w-[22rem] text-[11px] text-muted-foreground">
            {equipo.motivo}
          </div>
        )}
      </td>
      {CONTEXTO.map((c) => (
        <td key={c.key} className="whitespace-nowrap px-3 py-2 text-[12px]">
          {equipo.inventario?.[c.key] ?? "—"}
        </td>
      ))}
      {campos.map((c, i) => {
        const celda = equipo.celdas[c.key];
        const primeroDeGrupo = i === 0 || campos[i - 1]?.tipo !== c.tipo;
        return (
          <td
            key={c.key}
            className={`px-3 py-2 text-center ${primeroDeGrupo ? "border-l" : ""}`}
          >
            {detalle ? (
              <span
                className={`font-mono text-[11px] ${
                  celda?.estado === "deriva" ? "font-semibold text-red-600 dark:text-red-400" : ""
                }`}
              >
                {textoValor(celda?.valor)}
              </span>
            ) : (
              <Marca estado={celda?.estado ?? "sin_datos"} />
            )}
          </td>
        );
      })}
    </tr>
  );
}

function Resumen({
  n,
  etiqueta,
  tono,
  ayuda,
}: {
  n: number;
  etiqueta: string;
  tono?: "ok" | "mal" | "gris";
  ayuda?: string;
}) {
  const color =
    tono === "ok"
      ? "text-emerald-600 dark:text-emerald-400"
      : tono === "mal"
        ? "text-red-600 dark:text-red-400"
        : tono === "gris"
          ? "text-muted-foreground"
          : "";
  return (
    <div className="rounded-md border bg-card p-3">
      <div className={`text-2xl font-semibold tabular-nums ${color}`}>{n}</div>
      <div className="text-xs text-muted-foreground">{etiqueta}</div>
      {ayuda && <div className="mt-1 text-[11px] text-muted-foreground">{ayuda}</div>}
    </div>
  );
}
